import { useState, useEffect } from "react";
import { catalogService } from "@/services";

export interface DbSubStyle {
  id: string;
  style_id: string;
  display_name: string;
  emoji: string | null;
}

export interface DbHobby {
  id: string;
  display_name: string;
  emoji: string | null;
}

/**
 * Fetches taxonomy depth config, DB sub-styles, DB hobbies, and junction tables.
 * When tenantId is provided, tenant-specific mappings override global ones per style.
 * 
 * overriddenSubStyleIds: styles whose sub-styles have been customized (use tenant even if empty).
 * overriddenHobbyIds: styles whose hobbies/features have been customized (use tenant even if empty).
 */
export function useStyleTaxonomy(
  tenantId?: string,
  overriddenSubStyleIds?: string[],
  overriddenHobbyIds?: string[],
  depthOverrides?: Record<string, number>,
) {
  const [depthConfig, setDepthConfig] = useState<Record<string, number>>({});
  const [subStyles, setSubStyles] = useState<DbSubStyle[]>([]);
  const [hobbies, setHobbies] = useState<DbHobby[]>([]);
  const [styleSubStyleMap, setStyleSubStyleMap] = useState<Record<string, string[]>>({});
  const [styleHobbyMap, setStyleHobbyMap] = useState<Record<string, string[]>>({});
  const [subStyleHobbyMap, setSubStyleHobbyMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = () => setRefreshKey((k) => k + 1);

  const subOverridesKey = overriddenSubStyleIds?.join(",") ?? "";
  const hobbyOverridesKey = overriddenHobbyIds?.join(",") ?? "";
  const depthOverridesKey = depthOverrides ? JSON.stringify(depthOverrides) : "";

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const { styles, subStyles: subRes, hobbies: hobbyRes, styleSubStyles, styleHobbies } =
        await catalogService.fetchAllCatalogs(tenantId);

      const subOverrides = new Set(overriddenSubStyleIds || []);
      const hobbyOverrides = new Set(overriddenHobbyIds || []);

      if (styles.data) {
        const map: Record<string, number> = {};
        for (const row of styles.data as any[]) map[row.style_id] = row.max_depth;
        setDepthConfig(map);
      }
      if (subRes.data) setSubStyles(subRes.data as DbSubStyle[]);
      if (hobbyRes.data) setHobbies(hobbyRes.data as DbHobby[]);

      // Build sub-style junction map
      if (styleSubStyles.data) {
        const globalMap: Record<string, string[]> = {};
        const tenantMap: Record<string, string[]> = {};
        for (const row of styleSubStyles.data as any[]) {
          if (row.tenant_id) {
            if (!tenantMap[row.style_id]) tenantMap[row.style_id] = [];
            tenantMap[row.style_id].push(row.sub_style_id);
          } else {
            if (!globalMap[row.style_id]) globalMap[row.style_id] = [];
            globalMap[row.style_id].push(row.sub_style_id);
          }
        }
        const merged: Record<string, string[]> = {};
        const allStyleIds = new Set([...Object.keys(globalMap), ...Object.keys(tenantMap), ...subOverrides]);
        for (const styleId of allStyleIds) {
          if (subOverrides.has(styleId) || tenantMap[styleId]) {
            merged[styleId] = tenantMap[styleId] || [];
          } else {
            merged[styleId] = globalMap[styleId] || [];
          }
        }
        setStyleSubStyleMap(merged);
      }

      // Build hobby junction maps
      if (styleHobbies.data) {
        const globalStyleMap: Record<string, string[]> = {};
        const tenantStyleMap: Record<string, string[]> = {};
        const globalSubMap: Record<string, string[]> = {};
        const tenantSubMap: Record<string, string[]> = {};

        for (const row of styleHobbies.data as any[]) {
          const isTenant = !!row.tenant_id;
          const sMap = isTenant ? tenantStyleMap : globalStyleMap;
          const smMap = isTenant ? tenantSubMap : globalSubMap;

          if (!sMap[row.style_id]) sMap[row.style_id] = [];
          sMap[row.style_id].push(row.hobby_id);

          if (row.sub_style_id) {
            if (!smMap[row.sub_style_id]) smMap[row.sub_style_id] = [];
            smMap[row.sub_style_id].push(row.hobby_id);
          }
        }

        // Merge style→hobby with hobby-specific override awareness
        const mergedStyle: Record<string, string[]> = {};
        const allHobbyStyleIds = new Set([...Object.keys(globalStyleMap), ...Object.keys(tenantStyleMap), ...hobbyOverrides]);
        for (const styleId of allHobbyStyleIds) {
          if (hobbyOverrides.has(styleId) || tenantStyleMap[styleId]) {
            mergedStyle[styleId] = tenantStyleMap[styleId] || [];
          } else {
            mergedStyle[styleId] = globalStyleMap[styleId] || [];
          }
        }
        setStyleHobbyMap(mergedStyle);

        const mergedSub: Record<string, string[]> = { ...globalSubMap };
        for (const subId of Object.keys(tenantSubMap)) {
          mergedSub[subId] = tenantSubMap[subId];
        }
        setSubStyleHobbyMap(mergedSub);
      }

      setLoading(false);
    };
    fetchAll();
  }, [tenantId, refreshKey, subOverridesKey, hobbyOverridesKey, depthOverridesKey]);

  const getDepth = (styleId: string) => {
    if (depthOverrides && depthOverrides[styleId] !== undefined) return depthOverrides[styleId];
    return depthConfig[styleId] ?? 0;
  };
  const hasSubStyles = (styleId: string) => getDepth(styleId) >= 1;
  const hasHobbies = (styleId: string) => getDepth(styleId) >= 2;

  const getSubStylesFor = (styleId: string) => {
    const assignedIds = styleSubStyleMap[styleId] || [];
    return subStyles.filter((s) => assignedIds.includes(s.id));
  };

  const getHobbiesFor = (styleId: string) => {
    const assignedIds = styleHobbyMap[styleId] || [];
    return hobbies.filter((h) => assignedIds.includes(h.id));
  };

  const getHobbiesForSubStyle = (subStyleId: string) => {
    const assignedIds = subStyleHobbyMap[subStyleId] || [];
    return hobbies.filter((h) => assignedIds.includes(h.id));
  };

  return {
    depthConfig, subStyles, hobbies, loading, refetch,
    getDepth, hasSubStyles, hasHobbies, getSubStylesFor, getHobbiesFor, getHobbiesForSubStyle,
    styleSubStyleMap, styleHobbyMap, subStyleHobbyMap,
  };
}
